import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Chip,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import Header from "../../../../components/Header";
import {
  AssetFinanceUrl,
  customerservice,
  loanManagementUrl,
} from "../../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../../data/DynamicServiceNew/jsondynamicservice";

// ─────────────────────────────────────────────────────────────
// FILE STORAGE CONFIG — same gateway used by CollateralAsset
// ─────────────────────────────────────────────────────────────
const FILE_GATEWAY_URL = "https://api.sheriapro.sababisha.com";
const FILE_STORAGE_API_KEY =
  "E3VF4C6KFV43JH6GKCDFGJP45V36JHGV3H4C6F3GJC63HG405GH6V345GHHJ4623FJJL3HCVMO1P23PZ07W8";

const uploadFileToStorage = async (
  file,
  project = "maziwa",
  folder = "manual-repayments",
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("project", project);
  form.append("folder", folder);
  const response = await fetch(
    `${FILE_GATEWAY_URL}/filestorageservice/upload`,
    {
      method: "POST",
      headers: { "X-Api-Key": FILE_STORAGE_API_KEY },
      body: form,
    },
  );
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);
  return await response.json(); // { url, objectName, fileName, ... }
};

// ─────────────────────────────────────────────────────────────

const ACCEPTED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
];
const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8 MB

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const isTechnicalDbError = (msg) => {
  if (!msg || typeof msg !== "string") return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes("postgres") ||
    lower.includes("npgsql") ||
    lower.includes("does not exist") ||
    lower.includes("sqlstate") ||
    lower.includes("syntax error") ||
    lower.includes("42883") ||
    lower.includes("42703") ||
    lower.includes("42p01") ||
    lower.includes("exception data:") ||
    lower.includes("at npgsql") ||
    lower.includes("at dapper")
  );
};

const parseErrorMessages = (rawError) => {
  try {
    const parsed =
      typeof rawError === "string" ? JSON.parse(rawError) : rawError;
    if (Array.isArray(parsed)) {
      return parsed.map((item) =>
        isTechnicalDbError(String(item))
          ? "Unable to process request due to a server configuration issue. Kindly contact Admin for assistance."
          : String(item),
      );
    }
    if (typeof parsed === "string") {
      if (isTechnicalDbError(parsed)) {
        return [
          "Unable to process request due to a server configuration issue. Kindly contact Admin for assistance.",
        ];
      }
      return [parsed];
    }
    if (parsed?.Error) {
      const messageText = parsed.Error.match(/MessageText:\s*(.*?)(?:\r?\n|$)/);
      const extracted = messageText?.[1]?.trim();
      if (extracted) {
        if (isTechnicalDbError(extracted)) {
          return [
            "Unable to process request due to a server configuration issue. Kindly contact Admin for assistance.",
          ];
        }
        return [extracted];
      }
      if (isTechnicalDbError(parsed.Error)) {
        return [
          "Unable to process request due to a server configuration issue. Kindly contact Admin for assistance.",
        ];
      }
      const systemException = parsed.Error.match(/System\.Exception: (.*?)\./);
      if (systemException?.[1]) return [systemException[1]];
      return [parsed.Error];
    }
    if (parsed?.error) {
      if (isTechnicalDbError(String(parsed.error))) {
        return [
          "Unable to process request due to a server configuration issue. Kindly contact Admin for assistance.",
        ];
      }
      return [String(parsed.error)];
    }
    return [JSON.stringify(parsed)];
  } catch {
    if (isTechnicalDbError(String(rawError))) {
      return [
        "Unable to process request due to a server configuration issue. Kindly contact Admin for assistance.",
      ];
    }
    return [String(rawError)];
  }
};

const showErrorMessages = (rawError, title = "Error!") => {
  const messages = parseErrorMessages(rawError);
  if (messages.length > 1) {
    return Swal.fire({
      icon: "error",
      title,
      html: `<ul style="text-align:left; margin:0; padding-left:1.2em;">${messages
        .map((message) => `<li>${escapeHtml(message)}</li>`)
        .join("")}</ul>`,
    });
  }
  return Swal.fire({
    icon: "error",
    title,
    text: messages[0] || "An unknown error occurred.",
  });
};

const validationSchema = yup.object().shape({
  amount: yup
    .number()
    .typeError("Enter a valid amount")
    .positive("Amount must be greater than zero")
    .required("Amount is required"),
  narration: yup
    .string()
    .trim()
    .required("Receipt / Invoice / M-Pesa reference is required")
    .min(3, "Reference must be at least 3 characters"),
});

// ─── Payment Slip Picker ──────────────────────────────────────
const SlipPicker = ({ stagedSlip, slipError, onSelect, onRemove }) => {
  const inputRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onSelect(file);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setHovered(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onSelect(file);
  };

  return (
    <Box>
      {/* Label */}
      <Typography
        variant="subtitle2"
        fontWeight={700}
        mb={1}
        color={slipError ? "error.main" : "primary.main"}
        sx={{ letterSpacing: 0.3 }}
      >
        📎 Payment Slip *
      </Typography>

      {!stagedSlip ? (
        <Box
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setHovered(true);
          }}
          onDragLeave={() => setHovered(false)}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            border: "2.5px dashed",
            borderColor: slipError
              ? "error.main"
              : hovered
                ? "#1565c0"
                : "#1976d2",
            borderRadius: 2,
            py: 4,
            px: 3,
            cursor: "pointer",
            bgcolor: slipError
              ? "rgba(211,47,47,0.04)"
              : hovered
                ? "rgba(25,118,210,0.10)"
                : "rgba(25,118,210,0.05)",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "#1565c0",
              bgcolor: "rgba(25,118,210,0.10)",
            },
          }}
        >
          <AttachFileIcon
            sx={{
              fontSize: 44,
              color: slipError ? "error.main" : hovered ? "#1565c0" : "#1976d2",
              transition: "color 0.2s",
            }}
          />
          <Typography
            variant="body1"
            fontWeight={700}
            color={slipError ? "error.main" : "#1976d2"}
          >
            {hovered ? "Drop file here" : "Click or drag & drop to upload"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Accepted: PDF, JPG, PNG, WEBP — max 8 MB
          </Typography>
          <Box
            sx={{
              mt: 1,
              px: 3,
              py: 0.8,
              borderRadius: 5,
              bgcolor: slipError ? "error.main" : "#1976d2",
              color: "#fff",
              fontSize: "0.8rem",
              fontWeight: 700,
              letterSpacing: 0.5,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            Browse File
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            border: "2px solid #1976d2",
            borderRadius: 2,
            px: 2.5,
            py: 1.5,
            bgcolor: "rgba(25,118,210,0.07)",
          }}
        >
          <InsertDriveFileOutlinedIcon
            sx={{ color: "#1976d2", fontSize: 30 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              color="#1976d2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {stagedSlip.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(stagedSlip.size / 1024).toFixed(0)} KB — ready to upload
            </Typography>
          </Box>
          <Chip
            label="Attached ✓"
            size="small"
            sx={{
              bgcolor: "#1976d2",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.7rem",
            }}
          />
          <Tooltip title="Remove and choose a different file">
            <IconButton size="small" onClick={onRemove} color="error">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {slipError && (
        <Typography
          variant="caption"
          color="error"
          mt={0.75}
          display="block"
          fontWeight={600}
        >
          ⚠ {slipError}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleFileChange}
      />
    </Box>
  );
};

// ─── Main Form ────────────────────────────────────────────────
const ManualLoanRepaymentForm = (props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);

  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState("");

  // Payment slip state
  const [stagedSlip, setStagedSlip] = useState(null); // File object
  const [slipError, setSlipError] = useState("");
  const [uploadingSlip, setUploadingSlip] = useState(false);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      setCustomer(null);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await fetchJsonDynamic(
          "dynamicCustomerSearch",
          "customer",
          { searchTerm: searchTerm.trim(), approval: 2},
          customerservice.uri,
        );
        const parsed = response?.results ? JSON.parse(response.results) : null;
        const found = Array.isArray(parsed) ? parsed[0] : parsed;
        if (found && found.Id) {
          setCustomer(found);
        } else {
          setCustomer(null);
        }
      } catch (error) {
        setCustomer(null);
      }
      setSearching(false);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    const fetchLoans = async () => {
      setSelectedLoanId("");
      if (!customer?.Id) {
        setLoans([]);
        return;
      }
      setLoadingLoans(true);
      try {
        const response = await fetchJsonDynamic(
          "loanApplicationByCustomer",
          "loans",
          { customerId: Number(customer.Id), isLoanCleared: 0 },
          loanManagementUrl.uri,
        );
        const allLoans = response?.results ? JSON.parse(response.results) : [];
        const eligible = (Array.isArray(allLoans) ? allLoans : []).filter(
          (loan) => loan.LoanDisbursed === 1,
        );
        setLoans(eligible);
        if (eligible.length === 1) {
          setSelectedLoanId(eligible[0].Id);
        }
      } catch (error) {
        setLoans([]);
      } finally {
        setLoadingLoans(false);
      }
    };
    fetchLoans();
  }, [customer]);

  const selectedLoan = loans.find((loan) => loan.Id === selectedLoanId) || null;

  const resetAll = () => {
    setCustomer(null);
    setSearchTerm("");
    setLoans([]);
    setSelectedLoanId("");
    setStagedSlip(null);
    setSlipError("");
  };

  const handleSlipSelect = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setSlipError("Only PDF, JPG, PNG or WEBP files are accepted.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setSlipError("File exceeds the 8 MB limit.");
      return;
    }
    setStagedSlip(file);
    setSlipError("");
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!selectedLoanId) {
      Swal.fire(
        "Loan Required",
        "Please select the loan to post this payment against.",
        "warning",
      );
      setSubmitting(false);
      return;
    }

    // Payment slip is mandatory
    if (!stagedSlip) {
      setSlipError(
        "A payment slip is required. Please attach a receipt, deposit slip or screenshot.",
      );
      setSubmitting(false);
      return;
    }

    try {
      // ── 1. Upload slip if staged ──────────────────────────
      let paymentSlipUrl = null;
      let paymentSlipObjectName = null;

      if (stagedSlip) {
        setUploadingSlip(true);
        try {
          const minioResult = await uploadFileToStorage(stagedSlip);
          paymentSlipUrl = minioResult.url ?? minioResult.Url ?? null;
          paymentSlipObjectName =
            minioResult.objectName ?? minioResult.ObjectName ?? null;
        } catch (uploadErr) {
          setUploadingSlip(false);
          setSubmitting(false);
          await Swal.fire(
            "Upload Failed",
            "Could not upload the payment slip. Please try again or remove it and resubmit.",
            "error",
          );
          return;
        }
        setUploadingSlip(false);
      }

      // ── 2. Post the manual payment ────────────────────────
      const manualPayment = {
        customerId: Number(customer.Id),
        loanId: Number(selectedLoanId),
        amount: parseFloat(values.amount),
        manualPaymentId: null,
        action: 0,
        narration: values.narration
          ? String(values.narration).replace(/\\/g, "\\\\").replace(/"/g, '\\"')
          : null,
        paymentSlipUrl,
        paymentSlipObjectName,
      };

      const response = await executeAnyMutation(
        "insertManualPayment",
        manualPayment,
        "manualPayment",
        false,
        AssetFinanceUrl.uri,
      );

      if (response?.results) {
        props.onClose();
        props.onAction();
        resetForm();
        resetAll();
        await Swal.fire(
          "Success!",
          "Manual payment has been staged and is now pending approval.",
          "success",
        );
      } else if (response?.error) {
        await showErrorMessages(response.error);
      }
    } catch (error) {
      await Swal.fire(
        "Error!",
        "An error occurred while processing your request. Please try again later.",
        "error",
      );
    }
    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title="New Manual Payment"
        subtitle="Search for a customer, select their loan, then post a manual loan repayment for a checker to approve"
      />

      <TextField
        fullWidth
        variant="filled"
        label="Search Customer (Name, Phone or ID number)"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={!!customer}
        sx={{ mb: 2 }}
      />

      {searching && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">Searching…</Typography>
        </Box>
      )}

      {!customer && !searching && searchTerm.trim().length >= 3 && (
        <Typography variant="body2" color="error" mb={2}>
          No approved customer found matching "{searchTerm}".
        </Typography>
      )}

      {customer && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {customer.FirstName} {customer.OtherNames}
                </Typography>
                <Typography variant="body2">
                  Phone: {customer.MobileNumber}
                </Typography>
                <Typography variant="body2">
                  ID/Passport: {customer.IdNoOrPassport || "N/A"}
                </Typography>
              </Box>
              <IconButton
                onClick={resetAll}
                size="small"
                title="Search a different customer"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}

      {customer && loadingLoans && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">Checking disbursed loans…</Typography>
        </Box>
      )}

      {customer && !loadingLoans && loans.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No disbursed loans found for this customer.
        </Alert>
      )}

      {customer && !loadingLoans && loans.length > 1 && (
        <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
          <InputLabel>Select Loan</InputLabel>
          <Select
            value={selectedLoanId}
            onChange={(e) => setSelectedLoanId(e.target.value)}
          >
            <MenuItem value="" disabled>
              Select Loan
            </MenuItem>
            {loans.map((loan) => (
              <MenuItem key={loan.Id} value={loan.Id}>
                Loan #{loan.Id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedLoan && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              Loan Details
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fit, minmax(140px, 1fr))"
              gap={2}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Loan ID
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLoan.Id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Amount Disbursed
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(selectedLoan.AmountToDisburse)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Loan Balance
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(selectedLoan.LoanBalance)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Arrears Balance
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(selectedLoan.Arrears)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Arrears Overdue Days
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLoan.ArrearsOverDueDays || 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Expected Clear Date
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLoan.ExpectedClearDate
                    ? new Date(
                        selectedLoan.ExpectedClearDate,
                      ).toLocaleDateString()
                    : "-"}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {customer && selectedLoanId && (
        <Formik
          initialValues={{ amount: "", narration: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              >
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  inputMode="numeric"
                  label="Amount (KES) *"
                  name="amount"
                  value={
                    values.amount === ""
                      ? ""
                      : Number(values.amount).toLocaleString()
                  }
                  onChange={(e) => {
                    const raw = e.target.value
                      .replace(/,/g, "")
                      .replace(/[^0-9]/g, "");
                    setFieldValue("amount", raw);
                  }}
                  onBlur={handleBlur}
                  error={touched.amount && !!errors.amount}
                  helperText={touched.amount && errors.amount}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  label="Receipt / Invoice / M-Pesa Code *"
                  placeholder="e.g. QGH7X2ABCD, INV-00123, RCP-2025-001"
                  name="narration"
                  value={values.narration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.narration && !!errors.narration}
                  helperText={
                    (touched.narration && errors.narration) ||
                    "Enter the M-Pesa code, receipt number or invoice number for this payment"
                  }
                  sx={{ gridColumn: "span 2" }}
                />

                {/* ── Payment Slip Attachment ── */}
                <Box sx={{ gridColumn: "span 4" }}>
                  <SlipPicker
                    stagedSlip={stagedSlip}
                    slipError={slipError}
                    onSelect={handleSlipSelect}
                    onRemove={() => {
                      setStagedSlip(null);
                      setSlipError("");
                    }}
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                justifyContent="end"
                mt="20px"
                gap={1}
                alignItems="center"
              >
                {uploadingSlip && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Uploading slip…
                    </Typography>
                  </Box>
                )}
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting || uploadingSlip}
                >
                  {isSubmitting || uploadingSlip
                    ? "Submitting..."
                    : "Post Manual Payment"}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default ManualLoanRepaymentForm;
